import React, { useState } from 'react';
import ArrowDownIcon from '@assets/icons/table/arrow-down.svg';
import ArrowUpIcon from '@assets/icons/table/arrow-up.svg';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import { hidePagination } from '@utils/pagination.ts';

import type { BasePaginationProps } from '../pagination';
import { BasePagination } from '../pagination';
import { HeaderItem, HeaderItemContent, HeaderRow, OuterWrapper, TableWrapper } from './styled.tsx';
import type { TableCell, TableDataDescription } from './types.ts';
import { calculateColumnAndCellWidths, renderDataOrEmptyState, shouldAlignToRight } from './utils.tsx';

export type SortValue = { columnId: string | null; order: 'asc' | 'desc' | null };

interface TableProps {
    data: TableDataDescription;
    searchStateTexts?: {
        noFiltersNoDataHeaderText: string;
        noFiltersNoDataSubtitleText: string;
        filtersNoDataHeaderText: string;
        filtersNoDataSubtitleText: string;
    };
    fixedColumnFlexes?: Record<string, number>;
    activeFilters?: string[];
    searchTerm?: string;
    rightAlignmentColumns?: string[];
    onSortChange?: (sort: SortValue) => void;
    onPaginationChange?: (pageNumber: number, pageSize: number) => void;
}

const getIsColumnActive = (columnId: string, activeFilters: string[]) => activeFilters.includes(columnId);

const Table: React.FC<TableProps> = ({
    data,
    fixedColumnFlexes,
    searchStateTexts = {
        noFiltersNoDataHeaderText: '',
        noFiltersNoDataSubtitleText: '',
        filtersNoDataHeaderText: '',
        filtersNoDataSubtitleText: '',
    },
    activeFilters = [],
    searchTerm = '',
    rightAlignmentColumns = [],
    onSortChange = () => {},
    onPaginationChange = () => {},
}) => {
    const [sortState, setSortState] = useState<{
        columnId: string | null;
        order: 'asc' | 'desc' | null;
    }>({ columnId: null, order: null });
    const [currentPage, setCurrentPage] = useState<number>(data.currentPage);
    const [pageSize, setPageSize] = useState<number>(data.pageSize);

    const rows = data.rows;
    const headerColumns: TableCell[] = data.headerCells;
    const widths = calculateColumnAndCellWidths(headerColumns, fixedColumnFlexes);
    const userInfo = useAppSelector((state) => state.user.userInfo);
    const userPreferencesSettings = getUserPreferencesOrDefaults(userInfo);

    const startIndex = 0;
    const endIndex = startIndex + pageSize;
    const pagedRows = rows.slice(startIndex, endIndex);
    const totalItems = data.totalItems;

    const handlePageChange: BasePaginationProps['onChange'] = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
        onPaginationChange(page, size);
    };

    const onColumnClick = (columnId: string) => {
        setSortState((prev) => {
            if (prev.columnId !== columnId) {
                const newState = { columnId, order: 'asc' as const };
                dispatchSort(newState);

                return newState;
            }

            if (prev.order === 'asc') {
                const newState = { columnId, order: 'desc' as const };
                dispatchSort(newState);

                return newState;
            }

            dispatchSort({ columnId: null, order: null });

            return { columnId: null, order: null };
        });
    };

    const dispatchSort = (sort: SortValue) => onSortChange(sort);

    return (
        <>
            <OuterWrapper>
                <TableWrapper>
                    <thead>
                        <HeaderRow>
                            {headerColumns.map((column) => {
                                const isActive = getIsColumnActive(column.name, activeFilters || []);
                                const isSorted = column.name === sortState.columnId;
                                const showArrowUp = isSorted && sortState.order === 'asc';
                                const showArrowDown = isSorted && sortState.order === 'desc';

                                return (
                                    <HeaderItem
                                        key={column.name}
                                        $isActive={isActive}
                                        width={widths[column.name]}
                                        onClick={() => onColumnClick(column.name)}
                                    >
                                        <HeaderItemContent
                                            $shouldAlignToRight={shouldAlignToRight(rightAlignmentColumns, column.name)}
                                        >
                                            <div>{column.displayName}</div>
                                            {!!showArrowUp && <ArrowUpIcon />}
                                            {!!showArrowDown && <ArrowDownIcon />}
                                        </HeaderItemContent>
                                    </HeaderItem>
                                );
                            })}
                        </HeaderRow>
                    </thead>
                    <tbody>
                        {renderDataOrEmptyState(
                            pagedRows,
                            data.headerCells.length,
                            widths,
                            activeFilters,
                            searchTerm,
                            searchStateTexts,
                            rightAlignmentColumns,
                            userPreferencesSettings,
                        )}
                    </tbody>
                </TableWrapper>
            </OuterWrapper>
            {!hidePagination(totalItems) && (
                <BasePagination
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    total={totalItems}
                />
            )}
        </>
    );
};

export default Table;
